import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ReplaySubject, Observable, of } from 'rxjs';
import { mergeMap, tap, map, switchMap, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { State, toODataString } from '@progress/kendo-data-query';
import { DialogService, DialogRef, DialogCloseResult } from '@progress/kendo-angular-dialog';

import { PopConfirmComponent } from 'src/app/shared/component/pop-confirm/pop-confirm.component';
import { PopPasswordComponent } from 'src/app/module/security/auth/pop-password/pop-password.component';
import { appUser } from './user.model';
import { UserPopupComponent } from './user-popup/user-popup.component';
import { UtilService } from 'src/app/shared/util.service';
import { LocalStorage } from 'src/app/core/local-storage.enum';

const apiUrl = environment.apiUrl;

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private users: ReplaySubject<appUser[]> = new ReplaySubject<appUser[]>(1);
  public user: ReplaySubject<appUser> = new ReplaySubject<appUser>(1);
  public usersOdata: ReplaySubject<any> = new ReplaySubject<any>(1);
  private userList: appUser[] = []; // liste des usagers dans la cache
  private dialog!: DialogRef;

  constructor(private dialogService: DialogService, private http: HttpClient, private router: Router, private utilService: UtilService) {}

  initOne() {
    this.user.next(new appUser());
  }

  list(): Observable<appUser[]> {
    return this.http.get<appUser[]>(apiUrl + 'security/UserAccount/Get', {}).pipe(
      tap((response: any) => {
        this.users.next(response);
      })
    );
  }

  read(id: string, returnValue?: boolean): Observable<appUser> {
    return this.http.get<appUser>(apiUrl + 'security/UserAccount/' + id, {}).pipe(
      tap((response: any) => {
        if (returnValue) return response;
        else this.user.next(response);
      })
    );
  }

  create(user: any): Observable<any> {
    return this.http.post<any>(apiUrl + 'security/UserAccount/', user).pipe(
      tap((response: any) => {
        this.user.next(response);
      })
    );
  }

  update(user: any): Observable<any> {
    return this.http.put<any>(apiUrl + 'security/UserAccount/' + user.id, user).pipe(
      tap((response: any) => {
        this.user.next(response);
      })
    );
  }

  // CACHE

  /**
   * cette fonction retourne la liste de client mis en cache dans le localStorage.
   * si  la cache n'A pas été rafraichie depuis 20 secondes, on la rafraichie.
   * on fetch les données de l'API de facon recursive pour obtenir tous les records.
   * @param forceFetch
   * @returns la liste de tous les clients
   */

  listCache(forceFetch?: boolean): Observable<appUser[]> {
    let fetch = false;
    // read localStorage
    let ls = localStorage.getItem(LocalStorage.CACHE_USER);
    if (forceFetch) {
      fetch = true;
    } else if (ls != null && ls.length > 0) {
      let data = JSON.parse(ls!);
      let now = Date.now();
      // force to refresh every X seconds
      if (now - data.lastUpdate > 20000) {
        fetch = true;
      }
    } else {
      // empty localstorage
      fetch = true;
    }
    // fetch or return cache data
    if (fetch) {
      // reset cache et start fetching
      localStorage.removeItem(LocalStorage.CACHE_USER);
      return this.fetchOData(apiUrl + 'security/UserAccount?$select=id,userFullName,userName,isEnabled');
    } else {
      let data = JSON.parse(ls!);
      // update service variable
      this.userList = data.data;
      return of(data.data);
    }
  }

  /*
   * cette fonction fetch les données de l'API de facon recursive pour obtenir tous les records.
   */
  private fetchOData(url: string): Observable<appUser[]> {
    let uri = `${url}`;
    return this.http.get<any>(uri).pipe(
      switchMap((response) => {
        const data = response.value as appUser[];
        // fetch actual cache & update it
        let existingCache = JSON.parse(localStorage.getItem(LocalStorage.CACHE_USER)!) || { lastUpdate: 0, data: [] };
        existingCache.data = existingCache.data.concat(data);
        existingCache.lastUpdate = Date.now();
        localStorage.setItem(LocalStorage.CACHE_USER, JSON.stringify(existingCache));
        // update service variable
        this.userList = existingCache.data;
        // fetch next page if needed OR return data
        if (response['@odata.nextLink']) {
          return this.fetchOData(response['@odata.nextLink']).pipe(map((nextData) => data.concat(nextData)));
        } else {
          return of(data);
        }
      }),
      catchError((error) => {
        console.error('Error fetching data:', error);
        return of([]);
      })
    );
  }

  // ODATA

  odata(state: State): Observable<any> {
    let _state = this.utilService.makeStateCaseInsensitive(state);
    let uri = `${apiUrl}security/UserAccount?${toODataString(_state)}&$count=true`;
    return this.http.get<appUser>(uri).pipe(
      tap((response: any) => {
        this.usersOdata.next(response);
      })
    );
  }

  odataStr(state: State, oDataString: string): Observable<any> {
    let oDataFullString = this.utilService.getODataFullString(state, oDataString);
    let uri = `${apiUrl}security/UserAccount?${oDataFullString}&$count=true`;
    return this.http.get<any>(uri);
  }

  // POPUP USER LIST

  public popup(title: string, oDataStr: string): any {
    if (title == '') title = 'module.user.popup.title';
    const confirmObservable = new Observable((observer) => {
      // open dialog
      this.dialog = this.dialogService.open({
        content: UserPopupComponent,
        minWidth: 400,
        height: 800,
        maxWidth: 900,
        cssClass: 'si-dialog-grid',
      });
      // set default values
      const dialogData = this.dialog.content.instance as UserPopupComponent;
      dialogData.oDataStr = oDataStr;
      dialogData.title = title;
      dialogData.button_cancel = 'module.user.popup.button_cancel';
      // subscribe to response
      this.dialog.result.subscribe((result) => {
        if (result instanceof DialogCloseResult) {
          // cancel
          observer.next(false);
        } else {
          // confirm
          observer.next(result);
        }
      });
    });
    return confirmObservable;
  }

  /*
   * Affiche le popup pour changer le mot de passe de l'utilisateur connecté
   * L'utilisateur doit entrer son ancien mot de passe pour pouvoir le changer.
   */

  popChangeMyPassword(id: string) {
    let dialog: DialogRef;
    // open dialog
    dialog = this.dialogService.open({
      content: PopPasswordComponent,
      minWidth: 550,
    });

    // set default values
    const resetFormData = dialog.content.instance as PopPasswordComponent;
    resetFormData.modifySelf = true;
    // subscribe to response
    dialog.result.subscribe((result) => {
      if (result instanceof DialogCloseResult) {
        // cancel
      } else {
        // submit
        this.http
          .put<any>(apiUrl + 'security/UserAccount/ForceChangePassword', {
            userID: id,
            OldPassword: encodeURIComponent(resetFormData.oldPassword),
            NewPassword: encodeURIComponent(resetFormData.newPassword),
          })
          .subscribe((response) => {
            // notify  on success
            if (response && response.title == 'AUTH_CHANGEPASSWORD_OPERATION_SUCCEEDED')
              this.utilService.notify('module.auth.reset-password.success_reset_password', 'success');
          });
      }
    });
  }

  /*
   * Affiche le popup pour changer le mot de passe d'un utilisateur
   */

  popChangePassword(id: string) {
    let dialog: DialogRef;
    // open dialog
    dialog = this.dialogService.open({
      content: PopPasswordComponent,
      minWidth: 550,
    });

    // set default values
    const resetFormData = dialog.content.instance as PopPasswordComponent;

    // subscribe to response
    dialog.result.subscribe((result) => {
      if (result instanceof DialogCloseResult) {
        // cancel
      } else {
        // submit
        this.http
          .put<any>(apiUrl + 'security/UserAccount/ForceChangePassword', {
            userID: id,
            NewPassword: encodeURIComponent(resetFormData.newPassword),
          })
          .subscribe((response) => {
            // notify  on success
            if (response && response.title == 'AUTH_CHANGEPASSWORD_OPERATION_SUCCEEDED')
              this.utilService.notify('module.auth.reset-password.success_reset_password', 'success');
          });
      }
    });
  }

  /*
   * Force user to change password on next login
   */

  forceChangePassword(id: string) {
    let dialog: DialogRef;
    // open dialog
    dialog = this.dialogService.open({
      content: PopConfirmComponent,
      minWidth: 400,
    });
    // set default values
    const dialogData = dialog.content.instance as PopConfirmComponent;
    dialogData.title = 'module.user.edit.forceChangePassword.title';
    dialogData.message = 'module.user.edit.forceChangePassword.message';
    dialogData.button_no = 'module.user.edit.forceChangePassword.button_no';
    dialogData.button_yes = 'module.user.edit.forceChangePassword.button_yes';
    // subscribe to response
    dialog.result.subscribe((result) => {
      if (result instanceof DialogCloseResult) {
        // no
      } else {
        // yes
        this.update({
          id: id,
          mustChangePasswordNextLogin: true,
        }).subscribe((response) => {
          if (response && response.id) {
            this.utilService.notify('module.user.edit.forceChangePassword.success', 'success');
          }
        });
      }
    });
  }

  /*
   * Envoie le nouveau mot de passe de l'usager avec le token de validation
   * lorsque l'utilisateur est forcé de changer son mot de passe.
   */

  forceChangePasswordWithToken(userId: string, newPassword: string, token: string): Observable<any> {
    let encodedToken = encodeURIComponent(token);
    let MyUri = apiUrl + 'security/UserAccount/ForceChangePasswordWithToken';
    let MyBody = {
      userId: userId,
      newPassword: newPassword,
      token: encodedToken,
    };
    return this.http.post<any>(MyUri, MyBody);
  }

  /*
   * Envoie un email à l'utilisateur pour réinitialiser son mot de passe.
   */

  forgotPassword(userName: string): Observable<any> {
    let callBackURI = location.protocol + '//' + location.host + '/auth/reset-password';
    return this.http.post<any>(apiUrl + 'security/UserAccount/SendEmailForResetPasswordToken?UserName=' + userName + '&CallBackURI=' + callBackURI, {});
  }

  // USER PREFERENCES

  /*
   * Les users preferences sont stockées dans le localStorage. (phase 1)
   * ils sont donc associés au device sur lequel l'utilisateur les a spécifées.
   * ex: les valeurs de préférences de l'utilisateur sur son ordinateur ne seront pas les mêmes que sur son téléphone.
   */

  /*
   * Sauvegarder une clé/valeur dans les préférences de l'utilisateur.
   */

  saveUserPreference(key: string, value: any) {
    var userPrefString = localStorage.getItem(LocalStorage.USER_PREF);
    var userPref = JSON.parse(userPrefString!);
    if (!userPref) userPref = {};
    userPref[key] = value;
    localStorage.setItem(LocalStorage.USER_PREF, JSON.stringify(userPref));
  }

  /*
   * Récupérer une clé/valeur dans les préférences de l'utilisateur.
   */

  getUserPreference(key: string) {
    var userPrefString = localStorage.getItem(LocalStorage.USER_PREF);
    var userPref = JSON.parse(userPrefString!);
    if (!userPref) userPref = {};
    return userPref[key];
  }
}
